import React, { useState, useEffect } from 'react';
import './StatusModal.css';

function StatusModal({ onClose, collectedItems, setCollectedItems, activeProfile, setActiveProfile, bugData, fishData, seaData }) {
  const [selectedSave, setSelectedSave] = useState(activeProfile);
  const [saveNames, setSaveNames] = useState({
    save1: 'Save 1',
    save2: 'Save 2',
    save3: 'Save 3'
  });
  const [originalSaveNames, setOriginalSaveNames] = useState({
    save1: 'Save 1',
    save2: 'Save 2',
    save3: 'Save 3'
  });
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [profileCollections, setProfileCollections] = useState({
    save1: [],
    save2: [],
    save3: []
  });
  const [originalProfileCollections, setOriginalProfileCollections] = useState({
    save1: [],
    save2: [],
    save3: []
  });
  const [activeButton, setActiveButton] = useState(null); // 'catch-all', 'clear-all', or null

  // Load save names and profile collections from localStorage on component mount
  useEffect(() => {
    const savedNames = localStorage.getItem('saveNames');
    if (savedNames) {
      const parsedNames = JSON.parse(savedNames);
      setSaveNames(parsedNames);
      setOriginalSaveNames(parsedNames);
    }
    
    // Load profile collections from localStorage
    const savedProfiles = localStorage.getItem('profileCollections');
    if (savedProfiles) {
      const parsedProfiles = JSON.parse(savedProfiles);
      setProfileCollections(parsedProfiles);
      setOriginalProfileCollections(parsedProfiles);
    } else {
      // Initialize with current collection for save1 if no profiles exist
      const initialProfiles = {
        save1: collectedItems,
        save2: [],
        save3: []
      };
      setProfileCollections(initialProfiles);
      setOriginalProfileCollections(initialProfiles);
    }
  }, [collectedItems]);

  const handleRename = () => {
    setNewName(saveNames[selectedSave]);
    setIsRenaming(true);
  };

  const confirmRename = () => {
    if (newName.trim()) {
      setSaveNames(prev => ({
        ...prev,
        [selectedSave]: newName.trim()
      }));
    }
    setIsRenaming(false);
    setNewName('');
  };

  const cancelRename = () => {
    setIsRenaming(false);
    setNewName('');
  };

  const handleCatchAll = () => {
    // Get all creature names from all data arrays
    const allCreatureNames = [
      ...bugData.map(bug => bug.name),
      ...fishData.map(fish => fish.name),
      ...seaData.map(sea => sea.name)
    ];
    
    // Set all creatures as collected for the selected profile temporarily
    setProfileCollections(prev => ({
      ...prev,
      [selectedSave]: allCreatureNames
    }));
    setActiveButton('catch-all');
    console.log('Catch All clicked for', saveNames[selectedSave], '- all creatures marked as collected (temporary)');
  };

  const handleClearAll = () => {
    // Clear all collected items for the selected profile temporarily
    setProfileCollections(prev => ({
      ...prev,
      [selectedSave]: []
    }));
    setActiveButton('clear-all');
    console.log('Clear All clicked for', saveNames[selectedSave], '- all creatures cleared (temporary)');
  };

  const handleSave = () => {
    // Save the current saveNames to localStorage permanently
    localStorage.setItem('saveNames', JSON.stringify(saveNames));
    // Save the profile collections to localStorage permanently
    localStorage.setItem('profileCollections', JSON.stringify(profileCollections));
    // Set the current profile's collection as the active collection
    setCollectedItems(profileCollections[selectedSave]);
    // Update the active profile in the parent component
    setActiveProfile(selectedSave);
    console.log('Save clicked - names, profile collections, and active profile saved permanently:', saveNames, profileCollections, selectedSave);
    onClose(); // Close modal after saving
  };

  const handleCancel = () => {
    // Revert any unsaved changes back to original names
    setSaveNames(originalSaveNames);
    // Revert any unsaved collection changes
    setProfileCollections(originalProfileCollections);
    setActiveButton(null);
    setIsRenaming(false);
    setNewName('');
    console.log('Cancel clicked - all changes reverted');
    onClose(); // Close modal without saving
  };

  const handleDeleteAllData = () => {
    if (window.confirm('Are you sure you want to clear ALL data? This will reset all save names and collections for ALL profiles. This action cannot be undone.')) {
      // Reset all save names to defaults
      const defaultNames = {
        save1: 'Save 1',
        save2: 'Save 2',
        save3: 'Save 3'
      };
      setSaveNames(defaultNames);
      setOriginalSaveNames(defaultNames);
      
      // Clear all profile collections
      const emptyCollections = {
        save1: [],
        save2: [],
        save3: []
      };
      setProfileCollections(emptyCollections);
      setOriginalProfileCollections(emptyCollections);
      
      // Clear localStorage
      localStorage.removeItem('saveNames');
      localStorage.removeItem('profileCollections');
      localStorage.removeItem('activeProfile');
      localStorage.removeItem('collectedItems');
      
      // Reset active profile to save1
      setActiveProfile('save1');
      setSelectedSave('save1');
      setCollectedItems([]);
      
      setActiveButton(null);
      setIsRenaming(false);
      setNewName('');
      
      console.log('All data cleared - everything reset to defaults');
    }
  };
  return (
    <div className="status-modal-overlay" onClick={onClose}>
      <div className="status-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="status-modal-header">
          <h3>Save Data</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="status-modal-body">
          <div className="status-section">
            <div className="user-saves-header">
              <h4>User Saves</h4>
              <button className="delete-all-data-button" onClick={handleDeleteAllData}>
                Delete All Data
              </button>
            </div>
            <div className="save-dropdown-container">
              <div className="save-row">
                <select 
                  id="save-select" 
                  className="save-dropdown"
                  value={selectedSave}
                  onChange={(e) => {
                    setSelectedSave(e.target.value);
                    setActiveButton(null); // Reset active button when switching profiles
                  }}
                >
                  <option value="save1">{saveNames.save1}</option>
                  <option value="save2">{saveNames.save2}</option>
                  <option value="save3">{saveNames.save3}</option>
                </select>
                
                <div className="rename-section">
                  {!isRenaming ? (
                    <button className="rename-button" onClick={handleRename}>
                      Rename
                    </button>
                  ) : (
                    <div className="rename-input-container">
                      <input
                        type="text"
                        className="rename-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Enter new name"
                        maxLength="20"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') confirmRename();
                          if (e.key === 'Escape') cancelRename();
                        }}
                      />
                      <div className="rename-buttons">
                        <button className="confirm-button" onClick={confirmRename}>
                          ✓
                        </button>
                        <button className="cancel-button" onClick={cancelRename}>
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="profile-status">
              <p className="collection-count">
                {saveNames[selectedSave]}: {profileCollections[selectedSave]?.length || 0} creatures collected
              </p>
            </div>
            
            <div className="action-buttons">
              <button 
                className={`catch-all-button ${activeButton === 'catch-all' ? 'active' : ''}`}
                onClick={handleCatchAll}
              >
                Catch All
              </button>
              <button 
                className={`clear-all-button ${activeButton === 'clear-all' ? 'active' : ''}`}
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </div>
          </div>
          
          <div className="modal-footer">
            <div className="footer-buttons">
              <button className="modal-cancel-button" onClick={handleCancel}>
                Cancel
              </button>
              <button className="modal-save-button" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatusModal;